import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { MaskLines } from "@/components/Motion";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMove}
      className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-[#14291f] text-[#f3efe4]"
    >
      <motion.img
        src="/fotos/piscina-area-social/1.jpg"
        alt="Piscina de borda infinita com vista para a Serra da Mantiqueira"
        style={{ y, scale }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6 }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f17] via-[#0f1f17]/45 to-[#0f1f17]/30" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#c8e39a]"
        >
          <MapPin className="h-4 w-4" /> Santo Antônio do Pinhal · SP
        </motion.p>
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <MaskLines
              testid="hero-title"
              lines={["Entre pinhais", "e a Pedra", "do Baú."]}
              className="text-[13vw] font-bold leading-[0.9] tracking-[-0.045em] sm:text-7xl lg:text-[7.5rem]"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-[#e6e1d3] md:text-lg"
            >
              Chalés, moradas e vivendas com lareira, ofurô e piscinas climatizadas, cercados pelo verde da Serra da Mantiqueira.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/#acomodacoes"
                data-testid="hero-book-now-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#7ab51d] px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#14291f] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#8cc92a]"
              >
                Escolher acomodação
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
              </Link>
              <span data-testid="hero-price-from" className="text-sm text-[#e6e1d3]">
                16 acomodações · diárias a partir de <strong className="text-white">R$ 950,00</strong>
              </span>
            </motion.div>
          </div>

          <div className="hidden lg:col-span-4 lg:block" style={{ perspective: 1000 }}>
            <motion.figure
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0, y: 60, clipPath: "inset(100% 0 0 0)" }}
              animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.7, duration: 1.3, ease: [0.76, 0, 0.24, 1] }}
              className="relative ml-auto w-[88%] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
            >
              <img src="/fotos/morada-minas-12/3.jpg" alt="Pôr do sol na Morada Minas" className="aspect-[4/5] w-full object-cover" />
              <figcaption
                style={{ transform: "translateZ(50px)" }}
                className="absolute bottom-4 left-4 bg-[#f4f0e6] px-5 py-4 text-[#1a382b] shadow-xl"
              >
                <span className="block text-[11px] uppercase tracking-[0.24em] text-[#5b7a1a]">Morada Minas 12</span>
                <span className="block text-lg font-bold">Pôr do sol na Mantiqueira</span>
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
